import { useState, useCallback, useRef } from 'react'
import {
  generateShots,
  groupShotsToScenes,
  getTaskStatus
} from '../../../services/api/aiVideoGeneration'

const MAX_RETRIES = 10
const POLL_INTERVAL = 15000

/**
 * Custom hook to manage shot generation and scene mapping
 * Handles fetching shots, polling status, and formatting scene data
 */
export const useShotsGeneration = ({
  formData,
  setFormData,
  onLocationMapGenerate,
  onAudioClustering,
  parseJSONSafely,
  videoId
} = {}) => {
  const isFetchingShots = useRef(false)
  const [shotsGenerationProgress, setShotsGenerationProgress] = useState(0)

  /**
   * Format scene object from API response
   */
  const formatSceneObj = useCallback(sceneObj => {
    return Object.entries(sceneObj).map(([sceneKey, sceneValue]) => {
      const { scene_location = '', shots = [] } = sceneValue

      // Normalize shots to a consistent array of { key, value }
      let shotsToProcess = []
      if (Array.isArray(shots)) {
        shotsToProcess = shots.map(s => {
          const k = Object.keys(s)[0]
          return { key: k, value: s[k] }
        })
      } else if (typeof shots === 'object' && shots !== null) {
        shotsToProcess = Object.entries(shots).map(([k, v]) => ({
          key: k,
          value: v
        }))
      }

      const formattedShots = shotsToProcess.map(
        ({ key: shotKey, value: shotValue }, index) => {
          return {
            ...shotValue,
            id: `${sceneKey}-shot-${index + 1}`,
            key: shotKey,
            title: shotValue.title || '',
            initial_image_prompt:
              shotValue['initial image prompt'] ||
              shotValue.initial_image_prompt ||
              '',
            scenery: shotValue.scenery || '',
            shot_direction:
              shotValue['shot direction'] || shotValue.shot_direction || '',
            video_prompt:
              shotValue['video prompt'] || shotValue.video_prompt || '',
            voiceover_text:
              shotValue['voiceover text'] || shotValue.voiceover_text || '',
            lighting: shotValue.lighting || '',
            dialogue: shotValue.dialogue || {},
            characters: shotValue.characters || [],
            video_duration: shotValue.video_duration || ''
          }
        }
      )

      return {
        id: sceneKey,
        title: sceneKey,
        description: scene_location,
        shots: formattedShots,
        aggregated_voiceover: sceneValue.aggregated_voiceover || '',
        audio_duration: sceneValue.audio_duration || 0,
        audio_duration_estimated: sceneValue.audio_duration_estimated || false,
        estimated_audio_duration: sceneValue.estimated_audio_duration || null,
        audio_link: sceneValue.audio_link || '',
        scene_location: scene_location
      }
    })
  }, [])

  /**
   * Handle scene mapping from shot data
   */
  const handleScenesMapping = useCallback(
    async (data, isFineTuned) => {
      try {
        const payload = {
          input_data: data
        }

        const response = await groupShotsToScenes(payload)

        if (response.data && response.data.task_id) {
          await pollTaskStatus(response.data.task_id, data)
        } else {
          const sceneData = response.data && response.data.output
          const formattedSceneData = formatSceneObj(sceneData)

          console.log('from shots', data)

          setFormData(prev => ({
            ...prev,
            shotList: formattedSceneData,
            shotsApiRes: data,
            ...(data.location_map && {
              locationMap: parseJSONSafely?.(data.location_map)
            }),
            ...(data.character_map && {
              characterMap: parseJSONSafely?.(data.character_map)
            }),
            ...(data.outfit_map && {
              outfitMap: parseJSONSafely?.(data.outfit_map)
            }),
            ...(data.prop_map && {
              propMap: parseJSONSafely?.(data.prop_map)
            }),
            ...(data.props && {
              props: parseJSONSafely?.(data.props)
            }),
            ...(data.wardrobe && {
              wardrobe: parseJSONSafely?.(data.wardrobe)
            })
          }))

          if (onAudioClustering && formData?.selectedVoice) {
            await onAudioClustering(sceneData, data)
          }
        }
      } catch (error) {
        console.error('Scene mapping failed:', error)
      } finally {
        setFormData(prev => ({ ...prev, shotLoading: false }))
      }
    },
    [formatSceneObj, setFormData, parseJSONSafely, onAudioClustering, videoId]
  )

  /**
   * Poll task status for scene grouping
   */
  const pollTaskStatus = useCallback(
    async (taskId, originalData) => {
      while (true) {
        try {
          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            const sceneData = response.data.output
            const formattedSceneData = formatSceneObj(sceneData)

            setFormData(prev => ({
              ...prev,
              shotList: formattedSceneData,
              shotsApiRes: originalData,
              ...(originalData.location_map && {
                locationMap: parseJSONSafely?.(originalData.location_map)
              }),
              ...(originalData.character_map && {
                characterMap: parseJSONSafely?.(originalData.character_map)
              }),
              ...(originalData.outfit_map && {
                outfitMap: parseJSONSafely?.(originalData.outfit_map)
              }),
              ...(originalData.prop_map && {
                propMap: parseJSONSafely?.(originalData.prop_map)
              }),
              ...(originalData.props && {
                props: parseJSONSafely?.(originalData.props)
              }),
              ...(originalData.wardrobe && {
                wardrobe: parseJSONSafely?.(originalData.wardrobe)
              })
            }))

            if (originalData.location_map && onLocationMapGenerate) {
              onLocationMapGenerate(originalData.location_map)
            }

            if (onAudioClustering && formData?.selectedVoice) {
              await onAudioClustering(sceneData, originalData)
            }
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Task failed:', response.data)
            throw new Error('Task processing failed')
          } else {
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [
      formatSceneObj,
      setFormData,
      parseJSONSafely,
      onLocationMapGenerate,
      onAudioClustering
    ]
  )

  /**
   * Poll shots task status
   */
  const pollShotsTaskStatus = useCallback(
    async taskId => {
      while (true) {
        try {
          if (!isFetchingShots.current) break

          const response = await getTaskStatus(taskId)

          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.output
          ) {
            const shotsData = response.data.output
            await handleScenesMapping(shotsData)
            break
          } else if (response.data && response.data.status === 'failed') {
            console.error('Shots generation task failed:', response.data)
            throw new Error('Shots generation failed')
          } else {
            setShotsGenerationProgress(
              response.data && response.data.progress
                ? response.data.progress
                : shotsGenerationProgress
            )
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
          }
        } catch (error) {
          console.error('Error polling shots status:', error)
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      }
    },
    [handleScenesMapping, shotsGenerationProgress]
  )

  /**
   * Fetch shots from voiceover text
   */
  const fetchShots = useCallback(
    async (retryStyleId = null, retryCount = 0) => {
      // Handle legacy retryCount as first argument or styleId
      const styleIdArg = typeof retryStyleId === 'string' ? retryStyleId : null
      const actualRetryCount =
        typeof retryStyleId === 'number' ? retryStyleId : retryCount

      if (actualRetryCount === 0) {
        if (isFetchingShots.current) return
        isFetchingShots.current = true
      }

      setFormData(prev => ({ ...prev, shotLoading: true }))
      const storyText = formData && formData.voiceoverText
      // Use style_id from argument, then shotGenerationStyle object, fallback to 'realistic' key if old structure
      let styleId =
        styleIdArg ||
        formData?.shotGenerationStyle?.id ||
        formData?.shotGenerationStyle?.key ||
        null

      if (styleId === 'default_style') styleId = null

      try {
        const res = await generateShots({
          story: storyText,
          style_id: styleId,
          video_id: videoId
        })
        const taskId = res && res.data && res.data.task_id

        if (taskId) {
          await pollShotsTaskStatus(taskId)
        } else {
          const shotsData = res && res.data
          await handleScenesMapping(shotsData)
        }
      } catch (err) {
        if (actualRetryCount < MAX_RETRIES) {
          setTimeout(
            () =>
              fetchShots(
                styleIdArg || actualRetryCount + 1,
                actualRetryCount + 1
              ),
            POLL_INTERVAL
          )
        } else {
          setFormData(prev => ({ ...prev, shotLoading: false }))
        }
      } finally {
        if (actualRetryCount === 0) isFetchingShots.current = false
      }
    },
    [
      formData?.voiceoverText,
      setFormData,
      pollShotsTaskStatus,
      handleScenesMapping,
      videoId
    ]
  )

  /**
   * Reset shots generation state
   */
  const resetShotsGeneration = useCallback(() => {
    isFetchingShots.current = false
    setShotsGenerationProgress(0)
  }, [])

  return {
    // State
    isFetchingShots: isFetchingShots.current,
    shotsGenerationProgress,

    // Actions
    fetchShots,
    handleScenesMapping,
    resetShotsGeneration,

    // Utilities
    formatSceneObj
  }
}

export default useShotsGeneration
